import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {SpotlightAction} from "@mantine/spotlight";
import {useNavigate} from "react-router-dom";
import {SpotlightAction as MantineSpotlightAction} from "@mantine/spotlight/lib/types";
import {useAuth} from "../../providers/AuthProvider/AuthProvider";
import {PageActionDef, PageActionIconDef} from "../page/page-action-def";
import {otsPageActions} from "../page/ots-page-actions";

export const matchesQuery = (query: string | undefined, keywords: string[]): boolean => {
  return !query || keywords.findIndex((k) => k.toLowerCase().includes(query.toLowerCase())) >= 0
}

export type UseOtsSpotlightActionProps = {
  query?: string
}

export type UseOtsSpotlightActionResult = {
  actions: SpotlightAction[]
}

export const useOtsSpotlightAction = (props: UseOtsSpotlightActionProps = {}): UseOtsSpotlightActionResult => {
  const {query} = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])
  const navigate = useNavigate()

  const actions: MantineSpotlightAction[] = useMemo(() => {
    return otsPageActions
      .filter((action) => hasPageActionAccess(action, hasAccess))
      .filter((action) => matchesQuery(query, action.keywords))
      .map((action) => ({
        title: t(action.title.id, action.title.defaultMessage),
        description: action.description
          ? t(action.description.id, action.description.defaultMessage)
          : undefined,
        onTrigger: () => {
          navigate(action.path);
        },
        keywords: [...action.keywords],
        icon: renderPageActionIcon(action.icon, {size: 18}),
      }));
  }, [hasAccess, navigate, query, t])

  return {
    actions: actions
  }
}

export const renderPageActionIcon = (
  icon: PageActionIconDef | undefined,
  defaultProps: Record<string, unknown> = {}
) => {
  if (!icon) {
    return undefined;
  }

  const IconComponent = icon.component;
  return <IconComponent {...defaultProps} {...icon.props} />;
};

export const hasPageActionAccess = (
  action: PageActionDef,
  hasAccess: (...permissions: string[]) => boolean
): boolean => {
  if (!action.access) {
    return true;
  }

  if (Array.isArray(action.access)) {
    return hasAccess(...action.access);
  }

  return hasAccess(action.access);
};
